import { useEffect, useRef } from "react";
import { create } from "zustand";
import { api, type Model } from "@/api/client";

interface ModelsStore {
  models: Model[];
  loading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
}

export const useModelsStore = create<ModelsStore>((set) => ({
  models: [],
  loading: false,
  error: null,

  fetch: async () => {
    set({ loading: true });
    try {
      const models = await api.models.list();
      set({ models, loading: false, error: null });
    } catch (err: any) {
      set({ loading: false, error: err?.message || "Failed to load models" });
    }
  },
}));

/**
 * Calls `seed` once with the default model ids — the routed families Prism
 * preselects (ChatGPT / Claude / Gemini) — as soon as the catalog loads. Pass
 * `enabled: false` when the form already has a selection (e.g. report
 * templates) that must not be overridden.
 */
export function useDefaultModelSeed(enabled: boolean, seed: (ids: string[]) => void) {
  const models = useModelsStore((s) => s.models);
  const seeded = useRef(false);

  useEffect(() => {
    if (!enabled || seeded.current || models.length === 0) return;
    seeded.current = true;
    const ids = models.filter((m) => m.default).map((m) => m.id);
    if (ids.length > 0) seed(ids);
  }, [enabled, models, seed]);
}
